import { relations, sql } from 'drizzle-orm';
import { boolean, index, integer, jsonb, numeric, pgEnum, pgTable, text, uuid } from 'drizzle-orm/pg-core';
import * as z from 'zod';
import { Status } from '../../util/schemas.js';
import { user } from './auth.js';
import { drugCatalogItemsCategories, hazardClasses, medicineForms, solutionTypes, stabilityRules, units, } from './basic-data.js';
import { durationUnitEnum } from './enums.js';
import { tradeNames } from './products.js';
import { timestamps } from './utils/index.js';
import { userActions } from './utils/user-actions.js';
import { uuidv7 } from './utils/uuidv7.js';
import { doseCalcModeActiveEnum, preparationTypeEnum } from './enums.js';
// #region Enums
/* ------------------------------------------------------------------
 * Drug catalogue
 * ------------------------------------------------------------------ */
export const CatalogType = z.enum(['ACTIVE_SUBSTANCE', 'SUPPORTIVE_MEDICATION', 'SOLUTION']);
export const catalogTypeEnum = pgEnum('catalog_type', [
    CatalogType.enum.ACTIVE_SUBSTANCE,
    CatalogType.enum.SUPPORTIVE_MEDICATION,
    CatalogType.enum.SOLUTION,
]);
/* ------------------------------------------------------------------
 * Rounding & safety
 * ------------------------------------------------------------------ */
// TODO: rename RoundingMode to RoundingMode after removing products.ts
export const RoundingMode = z.enum(['NONE', 'NEAREST', 'UP', 'DOWN']);
export const rModeEnum = pgEnum('rounding_mode', [
    RoundingMode.enum.NONE,
    RoundingMode.enum.NEAREST,
    RoundingMode.enum.UP,
    RoundingMode.enum.DOWN,
]);
const roundingModeEnum = rModeEnum;
export const OverdosePreventionLevel = z.enum(['HARD_STOP', 'SOFT_WARNING', 'INFO']);
export const overdosePreventionLevelEnum = pgEnum('overdose_prevention_level', [
    OverdosePreventionLevel.enum.HARD_STOP,
    OverdosePreventionLevel.enum.SOFT_WARNING,
    OverdosePreventionLevel.enum.INFO,
]);
/* ------------------------------------------------------------------
 * Medication profiles (forms / behavior)
 * ------------------------------------------------------------------ */
export const AdminRoute = z.enum(['IV', 'PO', 'SC', 'IM', 'IT', 'OTHER']);
export const adminRouteEnum = pgEnum('admin_route', [
    AdminRoute.enum.IV,
    AdminRoute.enum.PO,
    AdminRoute.enum.SC,
    AdminRoute.enum.IM,
    AdminRoute.enum.IT,
    AdminRoute.enum.OTHER,
]);
export const AdminMethod = z.enum(['BOLUS', 'INFUSION', 'ORAL', 'OTHER']);
export const adminMethodEnum = pgEnum('admin_method', [
    AdminMethod.enum.BOLUS,
    AdminMethod.enum.INFUSION,
    AdminMethod.enum.ORAL,
    AdminMethod.enum.OTHER,
]);
export const OralQuantityBasis = z.enum(['DOSE_BASED', 'FIXED_UNITS']);
export const oralQuantityBasisEnum = pgEnum('oral_quantity_basis', [
    OralQuantityBasis.enum.DOSE_BASED,
    OralQuantityBasis.enum.FIXED_UNITS,
]);
export const DoseLimitValues = z.object({
    absolute: z.number({ message: 'invalid_number' }).optional(),
    bsa: z.number({ message: 'invalid_number' }).optional(),
    auc: z.number({ message: 'invalid_number' }).optional(),
    weight: z.number({ message: 'invalid_number' }).optional(),
});
export const DoseLimitsMatrix = z.object({
    minSingleDose: DoseLimitValues.optional(),
    maxSingleDose: DoseLimitValues.optional(),
    maxPerDay: DoseLimitValues.optional(),
    maxPerCycle: DoseLimitValues.optional(),
    maxLifetime: DoseLimitValues.optional(),
});
// #endregion
// #region Drug Catalogue (common table + subtype details)
export const drugCatalogItemStatus = pgEnum('drug_catalog_item_status', [Status.enum.active, Status.enum.inactive]);
export const drugCatalogItems = pgTable('drug_catalog_items', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    catalogType: catalogTypeEnum('catalog_type').notNull(),
    name: text('name').notNull(),
    prescribingName: text('prescribing_name'),
    code: text('manual_id').notNull().unique(),
    atcCode: text('atc_code'),
    nationalDrugId: text('national_drug_id'),
    description: text('description'),
    status: drugCatalogItemStatus('status').default(Status.enum.active),
    // denormalized "has at least one non-removed category link", so lists can filter items without
    // categories (Zero cannot run NOT EXISTS on the client); kept in sync by the drug item category mutators
    hasCategories: boolean('has_categories').notNull().default(false),
    ...timestamps(),
    ...userActions(),
}, t => [
    index('idx_drug_catalog_items_type_active').on(t.catalogType, t.status),
    index('idx_drug_catalog_items_name').on(t.name),
    index('idx_drug_catalog_items_active')
        .on(t.deletedAt)
        .where(sql `${t.deletedAt} IS NULL`),
    // the catalogue lists search the name with ILIKE
    index('idx_drug_catalog_items_name_trgm').using('gin', sql `${t.name} gin_trgm_ops`),
]);
export const activeSubstanceDetails = pgTable('active_substance_details', {
    drugCatalogItemId: uuid('drug_catalog_item_id')
        .primaryKey()
        .references(() => drugCatalogItems.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    doseCalcMode: doseCalcModeActiveEnum('dose_calc_mode').notNull(),
    doseUnitId: uuid('dose_unit_id')
        .references(() => units.id, { onDelete: 'restrict', onUpdate: 'cascade' })
        .notNull(),
    dosePrecision: integer('dose_precision'),
    doseLimits: jsonb('dose_limits').$type().default({}),
    roundingMode: roundingModeEnum('rounding_mode').notNull(),
    minIntervalValue: numeric('min_interval_value', { mode: 'number' }),
    minIntervalDurationUnit: durationUnitEnum('min_interval_unit'),
    overdosePreventionLevel: overdosePreventionLevelEnum('overdose_prevention_level').notNull(),
    hasSaltContent: boolean('has_salt_content'),
    saltContentNotes: text('salt_content_notes'),
    molarMassFreeBase: numeric('molar_mass_free_base', { mode: 'number' }),
    molarMassSaltBase: numeric('molar_mass_salt_base', { mode: 'number' }),
    saltFactor: numeric('salt_factor', { mode: 'number' }),
    additionalApprovalRequired: boolean('requires_additional_approval'),
    // label band: cytotoxic preparations print the organisation's cytotoxic band text
    cytotoxic: boolean('cytotoxic').notNull().default(true),
    // vinca alkaloids: the label prints the fixed "intravenous use only" warning
    routeWarning: boolean('route_warning').notNull().default(false),
    productionNotes: text('production_notes'),
    externalInformationLink: text('external_information_link'),
    preparationType: preparationTypeEnum('preparation_type'),
    ...timestamps(),
    ...userActions(),
}, t => [
    // the active-substances list filters by dose unit through an EXISTS lookup on this column
    index('idx_active_substance_details_dose_unit').on(t.doseUnitId),
]);
export const activeSubstanceHazardClasses = pgTable('active_substance_hazard_classes', {
    id: uuidv7('id').primaryKey(),
    activeSubstanceDetailId: uuid('active_substance_detail_id')
        .notNull()
        .references(() => activeSubstanceDetails.drugCatalogItemId, { onDelete: 'cascade', onUpdate: 'cascade' }),
    hazardClassId: uuid('hazard_class_id')
        .notNull()
        .references(() => hazardClasses.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    ...timestamps(),
    ...userActions(),
}, t => [index('idx_active_substance_hazard_classes_detail').on(t.activeSubstanceDetailId)]);
export const supportiveMedicationDetails = pgTable('supportive_medication_details', {
    drugCatalogItemId: uuid('drug_catalog_item_id')
        .primaryKey()
        .references(() => drugCatalogItems.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    doseCalcMode: doseCalcModeActiveEnum('dose_calc_mode').notNull(),
    doseUnitId: uuid('dose_unit_id')
        .references(() => units.id, { onDelete: 'restrict', onUpdate: 'cascade' })
        .notNull(),
    dosePrecision: integer('dose_precision').notNull(),
    doseLimits: jsonb('dose_limits').$type().default({}),
    roundingMode: roundingModeEnum('rounding_mode').notNull(),
    overdosePreventionLevel: overdosePreventionLevelEnum('overdose_prevention_level').notNull(),
    externalInformationLink: text('external_information_link'),
    ...timestamps(),
    ...userActions(),
});
export const solutionDetails = pgTable('solution_details', {
    drugCatalogItemId: uuid('drug_catalog_item_id')
        .primaryKey()
        .references(() => drugCatalogItems.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    concentrationValue: numeric('concentration_value').notNull(),
    concentrationUnitId: uuid('concentration_unit')
        .references(() => units.id, { onDelete: 'restrict', onUpdate: 'cascade' })
        .notNull(),
    ...timestamps(),
    ...userActions(),
});
export const solutionDetailTypes = pgTable('solution_detail_types', {
    id: uuidv7('id').primaryKey(),
    solutionDetailId: uuid('solution_detail_id')
        .notNull()
        .references(() => solutionDetails.drugCatalogItemId, { onDelete: 'cascade', onUpdate: 'cascade' }),
    solutionTypeId: uuid('solution_type_id')
        .notNull()
        .references(() => solutionTypes.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    ...timestamps(),
    ...userActions(),
}, t => [index('idx_solution_detail_types_detail').on(t.solutionDetailId)]);
// #endregion
// #region Medication Profiles (formerly "medicine_form")
export const medicationProfileStatus = pgEnum('medication_profile_status', [Status.enum.active, Status.enum.inactive]);
export const medicationProfiles = pgTable('medication_profiles', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    drugCatalogItemId: uuid('drug_catalog_item_id')
        .notNull()
        .references(() => drugCatalogItems.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    medicineFormId: uuid('medicine_form_id')
        .notNull()
        .references(() => medicineForms.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    name: text('display_name'),
    reconstitutionRequired: boolean('reconstitution_required').notNull(),
    isDrySubstance: boolean('is_dry_substance').notNull(),
    oralQuantityBasis: oralQuantityBasisEnum('oral_quantity_basis'),
    status: medicationProfileStatus('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
}, t => [
    index('idx_medication_profiles_item_active').on(t.drugCatalogItemId, t.status),
    index('idx_medication_profiles_active')
        .on(t.deletedAt)
        .where(sql `${t.deletedAt} IS NULL`),
]);
// Allowed reconstitution diluents (Carrier Solutions only)
export const medicationProfileAllowedReconstitutionDiluents = pgTable('medication_profile_allowed_reconstitution_diluents', {
    id: uuidv7('id').primaryKey(),
    medicationProfileId: uuid('medication_profile_id')
        .notNull()
        .references(() => medicationProfiles.id, { onDelete: 'cascade', onUpdate: 'cascade' }),
    solutionDrugId: uuid('solution_drug_id')
        .notNull()
        .references(() => drugCatalogItems.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    ...timestamps(),
    ...userActions(),
});
// Allowed carrier solutions for dilution/administration (Carrier Solutions only)
export const medicationProfileAllowedCarrierSolutions = pgTable('medication_profile_allowed_carrier_solutions', {
    id: uuidv7('id').primaryKey(),
    medicationProfileId: uuid('medication_profile_id')
        .notNull()
        .references(() => medicationProfiles.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    solutionDrugId: uuid('solution_drug_id')
        .notNull()
        .references(() => drugCatalogItems.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    ...timestamps(),
    ...userActions(),
});
// #endregion
// #region Stability (universal, profile-bound)
export const medicationStabilityRuleStatus = pgEnum('medication_stability_rule_status', [
    Status.enum.active,
    Status.enum.inactive,
]);
export const medicationStabilityRules = pgTable('medication_stability_rules', {
    id: uuidv7('id').primaryKey(),
    medicationProfileId: uuid('medication_profile_id')
        .notNull()
        .references(() => medicationProfiles.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    stabilityRuleId: uuid('stability_rule_id')
        .notNull()
        .references(() => stabilityRules.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    ...timestamps(),
    ...userActions(),
}, t => [index('idx_medication_stability_rules_profile').on(t.medicationProfileId)]);
// #endregion
// #region Relations
// -------------------- drug_catalog_items --------------------
export const drugCatalogItemsRelations = relations(drugCatalogItems, ({ one, many }) => ({
    activeSubstanceDetails: one(activeSubstanceDetails, {
        fields: [drugCatalogItems.id],
        references: [activeSubstanceDetails.drugCatalogItemId],
    }),
    supportiveMedicationDetails: one(supportiveMedicationDetails, {
        fields: [drugCatalogItems.id],
        references: [supportiveMedicationDetails.drugCatalogItemId],
    }),
    solutionDetails: one(solutionDetails, {
        fields: [drugCatalogItems.id],
        references: [solutionDetails.drugCatalogItemId],
    }),
    medicationProfiles: many(medicationProfiles),
    asAllowedCarrierForProfiles: many(medicationProfileAllowedCarrierSolutions),
    asAllowedDiluentForProfiles: many(medicationProfileAllowedReconstitutionDiluents),
    created: one(user, { fields: [drugCatalogItems.createdBy], references: [user.id] }),
    categories: many(drugCatalogItemsCategories),
}));
export const activeSubstanceDetailsRelations = relations(activeSubstanceDetails, ({ one, many }) => ({
    drugCatalogItem: one(drugCatalogItems, {
        fields: [activeSubstanceDetails.drugCatalogItemId],
        references: [drugCatalogItems.id],
    }),
    doseUnit: one(units, {
        fields: [activeSubstanceDetails.doseUnitId],
        references: [units.id],
    }),
    hazardClasses: many(activeSubstanceHazardClasses),
}));
export const activeSubstanceHazardClassesRelations = relations(activeSubstanceHazardClasses, ({ one }) => ({
    activeSubstanceDetails: one(activeSubstanceDetails, {
        fields: [activeSubstanceHazardClasses.activeSubstanceDetailId],
        references: [activeSubstanceDetails.drugCatalogItemId],
    }),
    hazardClass: one(hazardClasses, {
        fields: [activeSubstanceHazardClasses.hazardClassId],
        references: [hazardClasses.id],
    }),
}));
export const supportiveMedicationDetailsRelations = relations(supportiveMedicationDetails, ({ one }) => ({
    drugCatalogItem: one(drugCatalogItems, {
        fields: [supportiveMedicationDetails.drugCatalogItemId],
        references: [drugCatalogItems.id],
    }),
    doseUnit: one(units, {
        fields: [supportiveMedicationDetails.doseUnitId],
        references: [units.id],
    }),
}));
export const solutionDetailsRelations = relations(solutionDetails, ({ one, many }) => ({
    drugCatalogItem: one(drugCatalogItems, {
        fields: [solutionDetails.drugCatalogItemId],
        references: [drugCatalogItems.id],
    }),
    concentrationUnit: one(units, {
        fields: [solutionDetails.concentrationUnitId],
        references: [units.id],
    }),
    types: many(solutionDetailTypes),
}));
export const solutionDetailTypesRelations = relations(solutionDetailTypes, ({ one }) => ({
    solutionDetail: one(solutionDetails, {
        fields: [solutionDetailTypes.solutionDetailId],
        references: [solutionDetails.drugCatalogItemId],
    }),
    solutionType: one(solutionTypes, {
        fields: [solutionDetailTypes.solutionTypeId],
        references: [solutionTypes.id],
    }),
}));
// -------------------- medication_profiles --------------------
export const medicationProfilesRelations = relations(medicationProfiles, ({ one, many }) => ({
    drugCatalogItem: one(drugCatalogItems, {
        fields: [medicationProfiles.drugCatalogItemId],
        references: [drugCatalogItems.id],
    }),
    medicineForm: one(medicineForms, {
        fields: [medicationProfiles.medicineFormId],
        references: [medicineForms.id],
    }),
    allowedReconstitutionDiluents: many(medicationProfileAllowedReconstitutionDiluents),
    allowedCarrierSolutions: many(medicationProfileAllowedCarrierSolutions),
    stabilityRules: many(medicationStabilityRules),
    tradeNames: many(tradeNames),
}));
export const medicationProfileAllowedReconstitutionDiluentsRelations = relations(medicationProfileAllowedReconstitutionDiluents, ({ one }) => ({
    medicationProfile: one(medicationProfiles, {
        fields: [medicationProfileAllowedReconstitutionDiluents.medicationProfileId],
        references: [medicationProfiles.id],
    }),
    solutionDrug: one(drugCatalogItems, {
        fields: [medicationProfileAllowedReconstitutionDiluents.solutionDrugId],
        references: [drugCatalogItems.id],
    }),
}));
export const medicationProfileAllowedCarrierSolutionsRelations = relations(medicationProfileAllowedCarrierSolutions, ({ one }) => ({
    medicationProfile: one(medicationProfiles, {
        fields: [medicationProfileAllowedCarrierSolutions.medicationProfileId],
        references: [medicationProfiles.id],
    }),
    solutionDrug: one(drugCatalogItems, {
        fields: [medicationProfileAllowedCarrierSolutions.solutionDrugId],
        references: [drugCatalogItems.id],
    }),
}));
// -------------------- medication_stability_rules --------------------
export const medicationStabilityRulesRelations = relations(medicationStabilityRules, ({ one }) => ({
    medicationProfile: one(medicationProfiles, {
        fields: [medicationStabilityRules.medicationProfileId],
        references: [medicationProfiles.id],
    }),
    stabilityRule: one(stabilityRules, {
        fields: [medicationStabilityRules.stabilityRuleId],
        references: [stabilityRules.id],
    }),
}));
// #endregion
//# sourceMappingURL=drug-catalog.js.map